import { z } from 'zod';
import { ADMIN_PAGE_SIZE_MAX, ADMIN_REPORTS_PER_ROW } from '../constants/admin.js';
import { PROFILE_REPORT_REASONS, REPORT_REASONS } from '../constants/report.js';
import { characterSchema } from './character.js';
import { rankingEntrySchema, rankingSeasonSchema } from './ranking.js';
import { tournamentDetailSchema, tournamentSchema } from './tournament.js';
/**
 * 管理者の API（#250、`docs/specs/api.md` の「管理者の API」）。**管理画面だけが使う。** iOS は呼ばないので、
 * `packages/contracts/mobile` には置かない。
 */
/**
 * 一覧のページ送り。クエリ文字列なので数に直してから検証する。
 *
 * `limit` の上限は `ADMIN_PAGE_SIZE_MAX`（理由はそちら）。
 */
export const adminPageQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(ADMIN_PAGE_SIZE_MAX).optional(),
    offset: z.coerce.number().int().min(0).optional(),
});
/**
 * `GET /admin/characters`。`ownerId` を渡すと、その人の選手だけ。`hidden` が `true` なら非公開の選手だけ、`false` なら公開の
 * 選手だけ（#251）。
 */
export const adminCharactersQuerySchema = adminPageQuerySchema.extend({
    ownerId: z.string().min(1).optional(),
    hidden: z.enum(['true', 'false']).optional(),
});
/**
 * `GET /admin/users`。`suspended` が `true` なら利用停止中の人だけ、`false` なら利用できる人だけ（#317）。
 * `id` を渡すと、その人だけ（通報やキャラクター一覧から、止めたい人の行にたどり着くため）。
 */
export const adminUsersQuerySchema = adminPageQuerySchema.extend({
    suspended: z.enum(['true', 'false']).optional(),
    id: z.string().min(1).optional(),
});
/** 勝ち・負け・引き分け。 */
export const winLossRecordSchema = z.object({
    wins: z.number().int().nonnegative(),
    losses: z.number().int().nonnegative(),
    draws: z.number().int().nonnegative(),
});
/** ユーザー一覧の 1 行。 */
export const adminUserSchema = z.object({
    id: z.string(),
    /** ニックネーム。**空文字はなし**（ゲストを作った直後）。 */
    displayName: z.string(),
    avatarPresetId: z.string().optional(),
    /** 手描きのアバターの URL（R2 の公開 URL）。 */
    avatarImageUrl: z.string().url().optional(),
    /** 引き継ぎを設定した Clerk のユーザー ID。無ければゲストのまま。 */
    clerkUserId: z.string().optional(),
    /** 引き継ぎに使ったログインの方法（クライアントの申告。表示だけ）。 */
    linkMethod: z.string().optional(),
    createdAt: z.string(),
    /** 最後に API を使った日（日付が変わったときだけ更新する）。`0009` より前の行は無い。 */
    lastActiveAt: z.string().optional(),
    characterCount: z.number().int().nonnegative(),
    /** 利用停止にした日時（#317、ADR-034）。停止中だけ。 */
    suspendedAt: z.string().optional(),
});
/**
 * キャラクター一覧の 1 行。**選手の値は公開の形（`characterSchema`）のまま**で、持ち主と戦績を足す。
 *
 * - 公式の戦績（大会の試合）: `wins` / `losses` / `draws`
 * - 練習（CPU 戦）: `cpuRecord`
 * - ともだちと対戦: `friendRecord`。戦績の列に数えていない（ADR-024）ので、`battles` から数える
 * - 大会: `tournaments`。出た回数・順位ポイントの合計・優勝の回数
 */
export const adminCharacterSchema = characterSchema.extend({
    owner: z.object({ id: z.string(), displayName: z.string() }),
    friendRecord: winLossRecordSchema,
    tournaments: z.object({
        entries: z.number().int().nonnegative(),
        points: z.number().int().nonnegative(),
        championships: z.number().int().nonnegative(),
    }),
    /** 非公開にした日時（#251）。公開なら無い。 */
    hiddenAt: z.string().optional(),
});
/** 一覧の応答。`total` は絞り込んだ全件の数（ページ送りに使う）。 */
function adminPageSchema(item) {
    return z.object({
        items: z.array(item),
        total: z.number().int().nonnegative(),
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
    });
}
export const adminUserPageSchema = adminPageSchema(adminUserSchema);
export const adminCharacterPageSchema = adminPageSchema(adminCharacterSchema);
/** `GET /admin/reports`。`status` は未対応（`open`、既定）か対応済み（`resolved`）（#256）。 */
export const adminReportsQuerySchema = adminPageQuerySchema.extend({
    status: z.enum(['open', 'resolved']).optional(),
});
/**
 * 通報の 1 件（#287）。通報した人・理由・日時。
 *
 * **通報した人は管理者の API にだけ出す。** アプリの応答とログには出さない（持ち主に知られる経路を作らない。ADR-032 A1）。
 * `reporter.displayName` は今のニックネーム（空文字は未設定）。
 */
function adminReportEntrySchema(reasons) {
    return z.object({
        reporter: z.object({ id: z.string(), displayName: z.string() }),
        reason: z.enum(reasons),
        createdAt: z.string(),
    });
}
/**
 * 1 行に付ける通報の 1 件ずつ。**新しい順に `ADMIN_REPORTS_PER_ROW` 件まで**（`count` がそれより多ければ、残りは件数だけ）。
 * 範囲は `count` と同じ（未対応の一覧なら未対応の、対応済みの一覧なら対応済みの）。
 */
function adminReportEntriesSchema(reasons) {
    return z.array(adminReportEntrySchema(reasons)).max(ADMIN_REPORTS_PER_ROW);
}
/**
 * 通報の一覧の 1 行（#256）。**選手ごとにまとめる**（同じ選手への通報を 1 行にし、件数と理由の内訳を出す）。
 *
 * 通報した人は `reports` に出す（#287。管理者にだけ）。
 */
export const adminReportSchema = z.object({
    character: adminCharacterSchema,
    /** 通報の件数（未対応なら未対応の、対応済みなら対応済みの）。 */
    count: z.number().int().positive(),
    /** 理由ごとの件数。 */
    reasons: z.record(z.enum(REPORT_REASONS), z.number().int().nonnegative()),
    /** 通報の 1 件ずつ（通報した人・理由・日時）。新しい順。 */
    reports: adminReportEntriesSchema(REPORT_REASONS),
    firstReportedAt: z.string(),
    lastReportedAt: z.string(),
    /** 対応済みにした日時（対応済みの一覧だけ）。 */
    resolvedAt: z.string().optional(),
});
export const adminReportPageSchema = adminPageSchema(adminReportSchema);
/**
 * プロフィールの通報の一覧の 1 行（#265）。**人ごとにまとめる**。`user` は**通報された人**で、ユーザー一覧と同じ形
 * （今のニックネームとアバター）。
 *
 * 通報した人は `reports` に出す（#287。管理者にだけ）。
 */
export const adminProfileReportSchema = z.object({
    user: adminUserSchema,
    count: z.number().int().positive(),
    reasons: z.record(z.enum(PROFILE_REPORT_REASONS), z.number().int().nonnegative()),
    /** 通報の 1 件ずつ（通報した人・理由・日時）。新しい順。 */
    reports: adminReportEntriesSchema(PROFILE_REPORT_REASONS),
    firstReportedAt: z.string(),
    lastReportedAt: z.string(),
    resolvedAt: z.string().optional(),
});
export const adminProfileReportPageSchema = adminPageSchema(adminProfileReportSchema);
/**
 * 管理画面のランキングの 1 行（#288）。公開のランキング（`GET /rankings`）と**同じ並び・同じ順位・同じポイント**で、選手だけを
 * 管理画面の形（持ち主・戦績・非公開の日時）にする。
 */
export const adminRankingEntrySchema = rankingEntrySchema.extend({
    character: adminCharacterSchema,
});
/**
 * `GET /admin/rankings`。公開と同じ上位 50。ページ送りは無い。
 *
 * **公開と同じく今のシーズン**（日本時間の暦の月、#284）。どの月かを `season` で返す（1 日に空になって見えても、
 * 月が変わったからだと分かるように）。
 */
export const adminRankingsSchema = z.object({
    season: rankingSeasonSchema,
    items: z.array(adminRankingEntrySchema),
});
/**
 * `GET /admin/tournaments`（#289）。`type` で種類を、`characterId` でその選手が出た大会だけに絞る。
 * `REGIONAL` / `NATIONAL` は作らない（ADR-023）ので受け付けない。
 */
export const adminTournamentsQuerySchema = adminPageQuerySchema.extend({
    type: z.enum(['CPU', 'WORLD']).optional(),
    characterId: z.string().min(1).optional(),
});
/**
 * 大会の一覧の 1 行（#289）。大会の値（公開の `tournamentSchema`）に、一覧で見たいものを足す。
 *
 * - `separate`: 別のワールドカップ（#282）か
 * - `entryCounts`: 出た選手の数。人の選手と CPU の選手に分ける
 * - `champion`: 優勝した選手（決勝の勝者）。終わるまで無い。**管理者には伏せない**（非公開の選手も名前と絵を出す）
 */
export const adminTournamentSchema = tournamentSchema.extend({
    separate: z.boolean(),
    entryCounts: z.object({
        players: z.number().int().nonnegative(),
        cpu: z.number().int().nonnegative(),
    }),
    champion: z
        .object({
        id: z.string(),
        name: z.string(),
        originalImageUrl: z.string().optional(),
        owner: z.object({ id: z.string(), displayName: z.string() }),
    })
        .optional(),
});
export const adminTournamentPageSchema = adminPageSchema(adminTournamentSchema);
/**
 * 大会の詳細（`GET /admin/tournaments/:tournamentId`、#289）。形は公開のブラケット（`tournamentDetailSchema`）に合わせ、
 * `separate` を足し、`characters` を管理画面の形（持ち主・戦績・非公開の日時）にする。**管理者には伏せない。**
 */
export const adminTournamentDetailSchema = tournamentDetailSchema.extend({
    separate: z.boolean(),
    characters: z.array(adminCharacterSchema),
});
